import React, { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { authUser } from "@/_actions/authActions";
import { mutate } from "swr";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useLocation, Link } from "react-router";
import { toast } from "sonner";
import { getProfile } from "@/_actions/userActions";
import cognitoMessages from "@/constants/cognitoMessages";
import { usePostHog } from "posthog-js/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const LoginSchema = Yup.object().shape({
  username: Yup.string()
    .email("Ingresa un correo electrónico válido")
    .required("Este campo es requerido"),
  password: Yup.string().required("Este campo es requerido"),
});

export default function LoginForm() {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);

  // If we were redirected from RequireAuth, this holds the original path
  const returnTo = location.state?.from;

  const form = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const handleSubmit = async () => {
    const { username, password } = form.getValues();
    try {
      await authUser(username, password);
      await mutate("/session/");
      const profileData = await getProfile();

      if (profileData[0]?.contact?.mobile || profileData[0]?.contact?.whatsapp) {
        if (posthog) {
          posthog.identify(profileData[0].id, {
            email: profileData[0].email,
            name: `${profileData[0].first_name} ${profileData[0].last_name}`,
            type: "pro_user",
            phone: profileData[0].contact?.whatsapp || profileData[0].contact?.mobile,
          });
        }
        // If there's a return URL (e.g., from OAuth flow), go back there
        navigate(returnTo || "/contacts");
      } else {
        navigate(returnTo || "/inicio");
      }
    } catch (err) {
      toast(cognitoMessages[err.message] || "Ocurrió un error, intenta de nuevo más tarde");
      $log.error(err);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Iniciar sesión</CardTitle>
        <CardDescription>Ingresa tus credenciales para acceder a tu cuenta</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Correo electrónico</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="correo electrónico" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contraseña</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="contraseña"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link
                  data-splitbee-event="forgot-password"
                  to="/forgot-password"
                  className="text text-pretty hover:text-primary hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>

            <Button
              id="Form-IniciarSesion"
              data-splitbee-event="log-in"
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Iniciar sesión
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
